// frontend/src/features/portfolio-diversification-optimizer-pro/components/PortfolioProBuilder.tsx
//
// Institutional portfolio specification + box-constraint builder. Gives the
// user one-click presets, a debounced air-gapped ticker typeahead, relative
// weight inputs with live normalized bars, a lookback-window selector, and the
// optimizer box constraints (min_weight floor / max_weight cap) with real-time
// feasibility checks (N * min_w <= 1 and N * max_w >= 1).
//
// All user inputs live in the persisted useDiversificationProFormStore, so the
// latest holdings, lookback and box constraints survive navigation / refresh.
//
// Themed for the shared light/dark dashboard shell.

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  Briefcase,
  Lock,
  Plus,
  Play,
  RefreshCw,
  RotateCcw,
  Search,
  Sliders,
  Trash2,
} from "lucide-react";
import { useShallow } from "zustand/react/shallow";

import { useDataAwareLookback } from "../../../shared/hooks/useDataAwareLookback";
import { useDebounce } from "../../../shared/hooks/useDebounce";
import { formatHistorySpan, toCoverageTickers } from "../../../shared/utils/lookback";
import { useDiversificationProCoverage, useSymbolAutocomplete } from "../api";
import {
  DEFAULT_HOLDINGS,
  DEFAULT_LOOKBACK_YEARS,
  FORM_BOUNDS,
  MAX_HOLDINGS,
  MIN_HOLDINGS,
  TICKER_RE,
  buildProRequest,
  totalRawWeightOf,
  useDiversificationProFormStore,
  validateProForm,
} from "../store/useDiversificationProFormStore";
import type { HoldingRow, PortfolioProRequest, SymbolSuggestion } from "../types";

// Mirrors backend STANDARD_LOOKBACK_OPTIONS (lookback_years 1..10).
const STANDARD_LOOKBACK_OPTIONS: readonly number[] = [1, 2, 3, 5, 10];

interface PortfolioProBuilderProps {
  onSubmit: (request: PortfolioProRequest) => void;
  isLoading: boolean;
}

const PRESET_PORTFOLIOS: {
  label: string;
  description: string;
  holdings: readonly HoldingRow[];
}[] = [
  {
    label: "All-Weather Multi-Asset",
    description:
      "Balanced cross-asset allocation across equities, duration, gold & commodities",
    holdings: [
      { ticker: "SPY", name: "SPDR S&P 500 ETF", assetClass: "Equity", rawWeight: 30 },
      { ticker: "TLT", name: "iShares 20+ Year Treasury Bond", assetClass: "Fixed Income", rawWeight: 40 },
      { ticker: "IEF", name: "iShares 7-10 Year Treasury Bond", assetClass: "Fixed Income", rawWeight: 15 },
      { ticker: "GLD", name: "SPDR Gold Shares", assetClass: "Commodity", rawWeight: 7.5 },
      { ticker: "DBC", name: "Invesco DB Commodity Index", assetClass: "Commodity", rawWeight: 7.5 },
    ],
  },
  {
    label: "US Mega-Cap Tech + Hedges",
    description: "High-growth equity core paired with sovereign bonds and gold",
    holdings: DEFAULT_HOLDINGS,
  },
  {
    label: "Global Factor Core",
    description: "US Large, Nasdaq Growth, Small Cap Value, Emerging Markets & Gold",
    holdings: [
      { ticker: "SPY", name: "SPDR S&P 500 ETF", assetClass: "Equity", rawWeight: 35 },
      { ticker: "QQQ", name: "Invesco QQQ Trust", assetClass: "Equity", rawWeight: 20 },
      { ticker: "IWM", name: "iShares Russell 2000 ETF", assetClass: "Equity", rawWeight: 15 },
      { ticker: "EEM", name: "iShares MSCI Emerging Markets", assetClass: "Equity", rawWeight: 15 },
      { ticker: "GLD", name: "SPDR Gold Shares", assetClass: "Commodity", rawWeight: 15 },
    ],
  },
];

export const PortfolioProBuilder: React.FC<PortfolioProBuilderProps> = ({
  onSubmit,
  isLoading,
}) => {
  const {
    holdings,
    savedLookbackYears,
    minWeightPct,
    maxWeightPct,
    setField,
    setHoldings,
    loadPreset,
    autoFitBounds,
    reset,
  } = useDiversificationProFormStore(
    useShallow((s) => ({
      holdings: s.holdings,
      savedLookbackYears: s.lookbackYears,
      minWeightPct: s.minWeightPct,
      maxWeightPct: s.maxWeightPct,
      setField: s.setField,
      setHoldings: s.setHoldings,
      loadPreset: s.loadPreset,
      autoFitBounds: s.autoFitBounds,
      reset: s.reset,
    }))
  );

  // Typeahead search state
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { suggestions, isSearching } = useSymbolAutocomplete(searchQuery);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Probe the price store for how much overlapping history these holdings
  // have so the lookback selector only offers windows we can actually serve.
  const coverageTickers = useMemo(
    () => toCoverageTickers(holdings.map((h) => h.ticker), TICKER_RE),
    [holdings]
  );
  const debouncedTickers = useDebounce(coverageTickers, 400);
  const { data: coverage } = useDiversificationProCoverage(debouncedTickers);
  const { lookbackYears, setLookbackYears, lookbackOptions } = useDataAwareLookback(
    STANDARD_LOOKBACK_OPTIONS,
    coverage,
    savedLookbackYears
  );

  // Persist the data-aware clamp only once real coverage is known, so the
  // transient "no coverage yet" option list never overwrites a saved
  // max-available window (e.g. 7Y) during re-hydration.
  useEffect(() => {
    if (coverage) setField("lookbackYears", lookbackYears);
  }, [coverage, lookbackYears, setField]);

  const totalRawWeight = useMemo(() => totalRawWeightOf(holdings), [holdings]);

  // Validate box-constraint feasibility in real time.
  const constraintValidation = useMemo(
    () => validateProForm({ holdings, minWeightPct, maxWeightPct }),
    [holdings, minWeightPct, maxWeightPct]
  );

  const handleLookbackChange = (years: number) => {
    setLookbackYears(years);
    setField("lookbackYears", years);
  };

  const handleReset = () => {
    reset();
    setLookbackYears(DEFAULT_LOOKBACK_YEARS);
    setSearchQuery("");
    setIsDropdownOpen(false);
  };

  const handleAddSymbol = (item: SymbolSuggestion) => {
    const cleanSym = item.symbol.trim().toUpperCase();
    if (!cleanSym) return;
    if (holdings.some((h) => h.ticker === cleanSym) || holdings.length >= MAX_HOLDINGS) {
      setSearchQuery("");
      setIsDropdownOpen(false);
      return;
    }
    const defaultWeight =
      holdings.length > 0 ? Math.round(totalRawWeight / holdings.length) || 10 : 20;
    setHoldings((prev) => [
      ...prev,
      {
        ticker: cleanSym,
        name: item.name ?? undefined,
        assetClass: item.asset_class ?? undefined,
        rawWeight: defaultWeight,
      },
    ]);
    setSearchQuery("");
    setIsDropdownOpen(false);
  };

  const handleManualAdd = (e: React.FormEvent) => {
    e.preventDefault();
    const cleanSym = searchQuery.trim().toUpperCase();
    if (!cleanSym) return;
    if (suggestions.length > 0) {
      handleAddSymbol(suggestions[0]);
    } else {
      handleAddSymbol({ symbol: cleanSym });
    }
  };

  const handleRemoveHolding = (ticker: string) => {
    setHoldings((prev) => prev.filter((h) => h.ticker !== ticker));
  };

  const handleWeightChange = (ticker: string, value: string) => {
    const parsed = parseFloat(value);
    setHoldings((prev) =>
      prev.map((h) =>
        h.ticker === ticker
          ? { ...h, rawWeight: Number.isNaN(parsed) ? 0 : Math.max(0, parsed) }
          : h
      )
    );
  };

  const handleEqualizeWeights = () => {
    if (holdings.length === 0) return;
    const eq = Number((100 / holdings.length).toFixed(2));
    setHoldings((prev) => prev.map((h) => ({ ...h, rawWeight: eq })));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const request = buildProRequest({ holdings, lookbackYears, minWeightPct, maxWeightPct });
    if (!request) return;
    // Snapshot the exact submitted window (incl. any data-aware clamp).
    setField("lookbackYears", lookbackYears);
    onSubmit(request);
  };

  const fieldClass =
    "w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-950/80 dark:text-white dark:placeholder-slate-500";

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900/90">
      {/* Header & Presets Row */}
      <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 dark:border-slate-800 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2.5">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 ring-1 ring-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-400 dark:ring-indigo-500/30">
              <Briefcase className="h-4 w-4" />
            </span>
            <h2 className="text-lg font-semibold tracking-tight text-slate-800 dark:text-white">
              Institutional Portfolio Specification &amp; Constraint Builder
            </h2>
          </div>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Configure holdings, lookback horizon, and position box constraints for
            Ledoit-Wolf HRP, ERC Risk Parity, and Max-Diversification simulations.
          </p>
        </div>

        {/* Institutional Preset Buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500">
            Presets:
          </span>
          {PRESET_PORTFOLIOS.map((preset) => (
            <button
              key={preset.label}
              type="button"
              onClick={() => loadPreset(preset.holdings)}
              className="rounded-lg border border-slate-200 bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:border-indigo-400/50 hover:bg-slate-200 hover:text-slate-900 dark:border-slate-700/80 dark:bg-slate-800/70 dark:text-slate-200 dark:hover:border-indigo-500/50 dark:hover:bg-slate-800 dark:hover:text-white"
              title={preset.description}
            >
              {preset.label}
            </button>
          ))}
          <button
            type="button"
            onClick={handleReset}
            title="Inputs are saved automatically. Reset restores the defaults."
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-semibold text-slate-500 transition hover:bg-slate-100 hover:text-indigo-700 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-indigo-300"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Reset
          </button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-12">
        {/* Left 7 Cols: Symbol Search + Holdings Table */}
        <div className="space-y-4 xl:col-span-7">
          {/* Typeahead Search Bar */}
          <div className="relative">
            <div className="flex flex-wrap gap-2 sm:flex-nowrap">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onFocus={() => setIsDropdownOpen(true)}
                  onBlur={() => setTimeout(() => setIsDropdownOpen(false), 180)}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setIsDropdownOpen(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleManualAdd(e);
                    }
                  }}
                  placeholder="Add ticker or ETF (e.g., AAPL, SPY, TLT, GLD, VWCE.DE, BTCUSD)..."
                  className={fieldClass}
                />
                {isSearching && (
                  <RefreshCw className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400 dark:text-slate-500" />
                )}
              </div>
              <button
                type="button"
                onClick={handleManualAdd}
                disabled={!searchQuery.trim()}
                className="inline-flex items-center gap-1.5 rounded-xl bg-slate-100 px-4 py-2.5 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-200 disabled:opacity-40 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-700"
              >
                <Plus className="h-4 w-4" />
                Add Asset
              </button>
              <button
                type="button"
                onClick={handleEqualizeWeights}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-medium text-slate-600 transition hover:border-slate-300 hover:text-slate-900 dark:border-slate-700/80 dark:bg-slate-900 dark:text-slate-300 dark:hover:border-slate-600 dark:hover:text-white"
              >
                Equalize (1/N)
              </button>
            </div>

            {/* Autocomplete Dropdown */}
            {isDropdownOpen && suggestions.length > 0 && (
              <div className="absolute z-30 mt-1.5 max-h-64 w-full overflow-auto rounded-xl border border-slate-200 bg-white py-1 shadow-2xl dark:border-slate-700 dark:bg-slate-900">
                {suggestions.map((item) => {
                  const alreadyAdded = holdings.some((h) => h.ticker === item.symbol);
                  return (
                    <button
                      key={item.symbol}
                      type="button"
                      disabled={alreadyAdded}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleAddSymbol(item);
                      }}
                      className="flex w-full items-center justify-between px-4 py-2 text-left text-sm transition hover:bg-slate-100 disabled:opacity-40 dark:hover:bg-slate-800"
                    >
                      <div className="flex items-center gap-2.5 overflow-hidden">
                        <span className="font-mono font-semibold text-indigo-600 dark:text-indigo-400">
                          {item.symbol}
                        </span>
                        <span className="truncate text-slate-600 dark:text-slate-300">
                          {item.name || "Market Instrument"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {item.asset_class && (
                          <span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700">
                            {item.asset_class}
                          </span>
                        )}
                        {item.exchange && (
                          <span className="font-mono text-[11px] text-slate-400 dark:text-slate-500">
                            {item.exchange}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Holdings Allocation List */}
          <div className="max-h-72 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50/60 dark:border-slate-800 dark:bg-slate-950/50">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 border-b border-slate-200 bg-white/95 text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:border-slate-800 dark:bg-slate-900/95 dark:text-slate-400">
                <tr>
                  <th className="py-2.5 pl-4 pr-2">Ticker</th>
                  <th className="px-2 py-2.5">Relative Weight</th>
                  <th className="px-2 py-2.5">Normalized Share</th>
                  <th className="py-2.5 pl-2 pr-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200/70 dark:divide-slate-800/60">
                {holdings.map((h) => {
                  const normPct =
                    totalRawWeight > 0 ? (h.rawWeight / totalRawWeight) * 100 : 0;
                  return (
                    <tr key={h.ticker} className="group hover:bg-slate-100/60 dark:hover:bg-slate-900/40">
                      <td className="py-2.5 pl-4 pr-2">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-slate-900 dark:text-white">
                            {h.ticker}
                          </span>
                          {h.assetClass && (
                            <span className="rounded bg-slate-200/80 px-1.5 py-0.5 text-[10px] text-slate-600 dark:bg-slate-800/90 dark:text-slate-400">
                              {h.assetClass}
                            </span>
                          )}
                        </div>
                        {h.name && (
                          <div className="max-w-[180px] truncate text-xs text-slate-400 dark:text-slate-500">
                            {h.name}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2.5">
                        <input
                          type="number"
                          step="any"
                          min="0.01"
                          value={h.rawWeight || ""}
                          onChange={(e) => handleWeightChange(h.ticker, e.target.value)}
                          className="w-24 rounded-lg border border-slate-300 bg-white px-2.5 py-1 font-mono text-sm text-slate-900 focus:border-indigo-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                        />
                      </td>
                      <td className="px-2 py-2.5">
                        <div className="flex items-center gap-2.5">
                          <div className="h-2 w-24 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                            <div
                              className="h-full rounded-full bg-indigo-500 transition-all duration-200"
                              style={{ width: `${Math.min(100, normPct)}%` }}
                            />
                          </div>
                          <span className="font-mono text-xs text-slate-600 dark:text-slate-300">
                            {normPct.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                      <td className="py-2.5 pl-2 pr-4 text-right">
                        <button
                          type="button"
                          onClick={() => handleRemoveHolding(h.ticker)}
                          disabled={holdings.length <= MIN_HOLDINGS}
                          className="rounded-lg p-1.5 text-slate-400 transition hover:bg-rose-500/10 hover:text-rose-500 disabled:opacity-25 dark:text-slate-500 dark:hover:text-rose-400"
                          title="Remove holding"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right 5 Cols: Lookback + Box Constraints + Submit */}
        <form
          onSubmit={handleSubmit}
          className="flex flex-col justify-between rounded-xl border border-slate-200 bg-slate-50/60 p-5 dark:border-slate-800 dark:bg-slate-950/60 xl:col-span-5"
        >
          <div className="space-y-5">
            {/* Lookback Horizon Selector */}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Historical Estimation Lookback Window
              </label>
              <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-5">
                {lookbackOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleLookbackChange(opt.value)}
                    title={opt.label}
                    aria-pressed={lookbackYears === opt.value}
                    className={`rounded-lg px-1 py-2 text-xs font-semibold transition ${
                      lookbackYears === opt.value
                        ? "bg-indigo-600 text-white shadow-sm shadow-indigo-500/30"
                        : "border border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-800 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400 dark:hover:border-slate-700 dark:hover:text-slate-200"
                    }`}
                  >
                    {opt.shortLabel}
                  </button>
                ))}
              </div>
              {coverage && coverage.max_lookback_years > 0 && (
                <p className="mt-1.5 text-[11px] text-slate-400 dark:text-slate-500">
                  {formatHistorySpan(coverage.max_lookback_years)} of shared history for these
                  holdings.
                </p>
              )}
            </div>

            {/* Optimization Box Constraints */}
            <div className="space-y-3 border-t border-slate-200/80 pt-4 dark:border-slate-800/80">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  <Sliders className="h-3.5 w-3.5 text-indigo-500 dark:text-indigo-400" />
                  Optimizer Box Constraints
                </span>
                <button
                  type="button"
                  onClick={autoFitBounds}
                  className="text-xs font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                >
                  Auto-fit to {holdings.length} assets
                </button>
              </div>

              {/* Min Weight Floor */}
              <div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500 dark:text-slate-400">
                    Minimum Weight Floor (min_w)
                  </span>
                  <span className="font-mono font-semibold text-slate-900 dark:text-white">
                    {minWeightPct.toFixed(1)}%
                  </span>
                </div>
                <input
                  type="range"
                  min={FORM_BOUNDS.minWeightPct.min}
                  max={FORM_BOUNDS.minWeightPct.max}
                  step={FORM_BOUNDS.minWeightPct.step}
                  value={minWeightPct}
                  onChange={(e) => setField("minWeightPct", parseFloat(e.target.value))}
                  className="mt-1.5 h-1.5 w-full cursor-pointer appearance-none rounded-lg bg-slate-200 accent-indigo-500 dark:bg-slate-800"
                />
              </div>

              {/* Max Weight Cap */}
              <div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500 dark:text-slate-400">
                    Maximum Weight Cap (max_w)
                  </span>
                  <span className="font-mono font-semibold text-slate-900 dark:text-white">
                    {maxWeightPct.toFixed(1)}%
                  </span>
                </div>
                <input
                  type="range"
                  min={FORM_BOUNDS.maxWeightPct.min}
                  max={FORM_BOUNDS.maxWeightPct.max}
                  step={FORM_BOUNDS.maxWeightPct.step}
                  value={maxWeightPct}
                  onChange={(e) => setField("maxWeightPct", parseFloat(e.target.value))}
                  className="mt-1.5 h-1.5 w-full cursor-pointer appearance-none rounded-lg bg-slate-200 accent-indigo-500 dark:bg-slate-800"
                />
              </div>

              <div className="flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-[11px] text-slate-500 dark:bg-slate-900/90 dark:text-slate-400">
                <Lock className="h-3.5 w-3.5 shrink-0 text-slate-400 dark:text-slate-500" />
                <span>
                  Simulations (HRP, ERC Risk Parity, Max-Div) are constrained to{" "}
                  <strong className="font-mono text-slate-700 dark:text-slate-200">
                    [{minWeightPct}%, {maxWeightPct}%]
                  </strong>{" "}
                  per holding.
                </span>
              </div>
            </div>
          </div>

          {/* Feasibility Alert + Run CTA */}
          <div className="mt-5 space-y-3">
            {!constraintValidation.valid && constraintValidation.message && (
              <div className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400" />
                <span>{constraintValidation.message}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={!constraintValidation.valid || isLoading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-600/25 transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Running 5-Pillar Institutional Engine...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 fill-current" />
                  Run Institutional Diversification &amp; Optimization
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
